import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import {
  BID_RESULT_LABELS,
  daysLeft,
  daysLeftLabel,
  pluralDays,
  DELIVERY_CONFIRMABLE_STATUSES,
  DELIVERY_STAGE_STATUSES,
  DELIVERY_STATUS_LABELS,
  DOC_SOURCE_LABELS,
  formatDims,
  formatTenge,
  LOT_EVENT_LABELS,
  LOT_STATUS_LABELS,
  TRANSPORT_KIND_LABELS,
  vatLabel,
  WINNER_DOC_LABELS,
  WINNER_STAGE_LABELS,
  type AdminLotBid,
  type LotDetail,
  type LotDetailEvent,
  type LotStatus,
  type LotWinner,
  type WinnerDocStage,
  type WinnerDocType,
  type WinnerPatch,
} from "@omts/shared";
import { adminApi, api, fieldErrorsOf, subscribeEvents } from "../api";
import { useAuth } from "../auth";
import { LotChat } from "../components/LotChat";
import { Modal } from "../components/Modal";
import { APP_TZ, fmtDateTime, fmtTime, isoToLocalInput, localInputToIso, timeLeft } from "../time";
import { statusBadgeClass } from "./LotsPage";
import { lotToTemplateFields } from "./NewLotPage";

const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Неизвестная ошибка");

const FINISHED: LotStatus[] = ["awaiting_winner_data", "formalized_pre_trip", "delivered", "closed", "paid"];
/** Документы, которые логист загружает на каждом этапе (первый — по умолчанию в селекте). */
const PRE_TRIP_DOC_TYPES: WinnerDocType[] = ["driver_id", "tech_passport", "other"];
const DELIVERY_DOC_TYPES: WinnerDocType[] = ["cmr", "border_pass", "invoice", "avr", "other"];

type Action = "extend" | "finish" | "cancel" | "template" | null;

/** «Сохранить как шаблон» (16.5): все поля лота, кроме дат, под указанным названием. */
function SaveTemplateModal({ lot, onClose, onDone }: { lot: LotDetail["lot"]; onClose: () => void; onDone: (name: string) => void }) {
  const [name, setName] = useState(`${lot.loadingAddress} → ${lot.unloadingAddress}`.slice(0, 120));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Введите название шаблона");
    setBusy(true);
    setError(null);
    try {
      const t = await api.createLotTemplate({ name: name.trim(), fields: lotToTemplateFields(lot) });
      onDone(t.name);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Сохранить ${lot.number} как шаблон`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <p className="muted">В шаблон попадут все поля заявки, кроме даты погрузки и дедлайна. Шаблоны лежат в Справочниках → «Шаблоны заявок».</p>
        <label className={`field${error ? " invalid" : ""}`}>
          <span>Название шаблона</span>
          <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoFocus />
          {error && <small className="field-error">{error}</small>}
        </label>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Сохраняем…" : "Сохранить шаблон"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ExtendModal({ lot, onClose, onDone }: { lot: LotDetail["lot"]; onClose: () => void; onDone: () => void }) {
  const [deadline, setDeadline] = useState(isoToLocalInput(lot.deadlineAt));
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const iso = localInputToIso(deadline);
    const next: Record<string, string> = {};
    if (!iso) next.deadlineAt = "Укажите дату и время";
    else if (new Date(iso).getTime() <= Date.now()) next.deadlineAt = "Новый дедлайн должен быть в будущем";
    if (!reason.trim()) next.reason = "Укажите причину";
    setErrors(next);
    if (Object.keys(next).length || !iso) return;
    setBusy(true);
    try {
      await api.extendLot(lot.id, iso, reason.trim());
      onDone();
    } catch (err) {
      setErrors({ ...fieldErrorsOf(err), form: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Продлить торги по ${lot.number}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <p className="muted">
          Текущий дедлайн: {fmtDateTime(lot.deadlineAt)}. Перевозчики получат уведомление о продлении.
        </p>
        <label className={`field${errors.deadlineAt ? " invalid" : ""}`}>
          <span>Новый дедлайн ({APP_TZ})</span>
          <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} autoFocus />
          {errors.deadlineAt && <small className="field-error">{errors.deadlineAt}</small>}
        </label>
        <label className={`field${errors.reason ? " invalid" : ""}`}>
          <span>Причина</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Например: мало ставок, ждём ещё перевозчиков" />
          {errors.reason && <small className="field-error">{errors.reason}</small>}
        </label>
        {errors.form && <p className="error">{errors.form}</p>}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Продлеваем…" : "Продлить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FinishModal({ data, onClose, onDone }: { data: LotDetail; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leader = data.bids.length ? data.bids[data.bids.length - 1] : null;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.finishLotEarly(data.lot.id);
      onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Завершить торги по ${data.lot.number} досрочно`} onClose={onClose}>
      {leader ? (
        <p>
          Победителем станет текущий лидер со ставкой{" "}
          <b>
            {formatTenge(leader.amount)} {vatLabel(data.lot.transportKind)}
          </b>{" "}
          (ставка № {leader.n}, {fmtDateTime(leader.placedAt)}). Приём ставок прекратится сразу.
        </p>
      ) : (
        <p>
          Ставок пока нет — заявка завершится <b>без победителя</b>. Приём ставок прекратится сразу.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Отмена
        </button>
        <button type="button" className="btn primary" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Завершаем…" : "Завершить досрочно"}
        </button>
      </div>
    </Modal>
  );
}

function CancelModal({ lot, onClose, onDone }: { lot: LotDetail["lot"]; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setError("Укажите причину отмены");
    setBusy(true);
    setError(null);
    try {
      await api.cancelLot(lot.id, reason.trim());
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Отменить заявку ${lot.number}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <p className="muted">
          {lot.status === "active"
            ? "Перевозчики получат уведомление об отмене. Отменённую заявку нельзя вернуть — но можно создать копию."
            : "Черновик будет помечен как отменённый."}
        </p>
        <label className={`field${error ? " invalid" : ""}`}>
          <span>Причина отмены</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          {error && <small className="field-error">{error}</small>}
        </label>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Не отменять
          </button>
          <button type="submit" className="btn danger" disabled={busy}>
            {busy ? "Отменяем…" : "Отменить заявку"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------- блок победителя ---------- */

type WinnerFormState = { driverFullName: string; driverPhone: string; truckPlate: string; trailerPlate: string; notes: string };
const fromCard = (w: LotWinner): WinnerFormState => ({
  driverFullName: w.card.driverFullName ?? "",
  driverPhone: w.card.driverPhone ?? "",
  truckPlate: w.card.truckPlate ?? "",
  trailerPlate: w.card.trailerPlate ?? "",
  notes: w.card.notes,
});

function WinnerBlock({ data, winner }: { data: LotDetail; winner: LotWinner }) {
  const [form, setForm] = useState<WinnerFormState>(() => fromCard(winner));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  /** Поля, которые логист сохранил из портала в этой сессии — иначе ФИО и телефон считаем пришедшими от бота. */
  const [editedHere, setEditedHere] = useState<Set<keyof WinnerFormState>>(new Set());

  // Данные пришли от бота (SSE) — подхватываем, если логист ещё не начал править.
  useEffect(() => {
    if (!dirty) setForm(fromCard(winner));
  }, [winner.card.updatedAt, dirty, winner]);

  const set = (key: keyof WinnerFormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const patch: WinnerPatch = {};
    const changed = new Set(editedHere);
    const current = fromCard(winner);
    (Object.keys(form) as (keyof WinnerFormState)[]).forEach((k) => {
      if (form[k] !== current[k]) {
        patch[k] = form[k].trim();
        changed.add(k);
      }
    });
    try {
      if (Object.keys(patch).length) await api.updateWinner(data.lot.id, patch);
      setEditedHere(changed);
      setDirty(false);
      setSavedAt(Date.now());
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  /** Источник текстового поля: от бота (Telegram), если заполнено и логист его здесь не менял. */
  const source = (key: "driverFullName" | "driverPhone") => {
    const value = winner.card[key];
    if (!value) return null;
    return editedHere.has(key) ? <span className="tag">изменено в портале</span> : <span className="tag tg">из Telegram</span>;
  };
  const portalSource = (key: "truckPlate" | "trailerPlate") =>
    winner.card[key] ? <span className="tag">{editedHere.has(key) ? "изменено в портале" : "указал логист"}</span> : null;

  const vat = vatLabel(data.lot.transportKind);

  return (
    <section className="card winner">
      <div className="row between">
        <h3>Победитель</h3>
        <span className="badge">{WINNER_STAGE_LABELS[winner.card.stage]}</span>
      </div>
      <dl className="props">
        <dt>Компания</dt>
        <dd>
          {winner.company?.name ?? "—"}
          {winner.company?.bin && <span className="muted"> · БИН {winner.company.bin}</span>}
        </dd>
        <dt>Менеджер</dt>
        <dd>
          {winner.contact ? (
            <>
              {winner.contact.fullName} · {winner.contact.phone}
              {winner.contact.telegramUsername && <span className="muted"> · @{winner.contact.telegramUsername}</span>}
            </>
          ) : (
            "—"
          )}
        </dd>
        <dt>Сумма</dt>
        <dd>
          {winner.amount === null ? (
            "—"
          ) : (
            <b className="price">
              {formatTenge(winner.amount)} {vat}
            </b>
          )}
        </dd>
        <dt>Время ставки</dt>
        <dd>{fmtDateTime(winner.placedAt)}</dd>
      </dl>

      <h4>Этап 1 — до рейса</h4>
      <p className="muted">
        ФИО и телефон водителя бот запрашивает у победителя сразу после торгов; логист может дозаполнить или поправить.
      </p>
      <form onSubmit={(e) => void save(e)} noValidate className="form-grid">
        <label className="field">
          <span>ФИО водителя {source("driverFullName")}</span>
          <input value={form.driverFullName} onChange={(e) => set("driverFullName", e.target.value)} />
        </label>
        <label className="field">
          <span>Телефон водителя {source("driverPhone")}</span>
          <input value={form.driverPhone} onChange={(e) => set("driverPhone", e.target.value)} inputMode="tel" placeholder="+7 7xx xxx xx xx" />
        </label>
        <label className="field">
          <span>Гос. номер тягача {portalSource("truckPlate")}</span>
          <input value={form.truckPlate} onChange={(e) => set("truckPlate", e.target.value)} />
        </label>
        <label className="field">
          <span>Гос. номер прицепа {portalSource("trailerPlate")}</span>
          <input value={form.trailerPlate} onChange={(e) => set("trailerPlate", e.target.value)} />
        </label>
        <label className="field full">
          <span>Заметки</span>
          <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </label>
        {error && <p className="error full">{error}</p>}
        <div className="form-actions full">
          <button type="submit" className="btn primary" disabled={busy || !dirty}>
            {busy ? "Сохраняем…" : "Сохранить"}
          </button>
          {dirty && !busy && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setForm(fromCard(winner));
                setDirty(false);
              }}
            >
              Отменить изменения
            </button>
          )}
          {savedAt && !dirty && <span className="muted">Сохранено {fmtTime(new Date(savedAt))}</span>}
        </div>
      </form>

      <h4>Документы до рейса</h4>
      <WinnerDocuments lotId={data.lot.id} documents={winner.documents} stage="pre_trip" docTypes={PRE_TRIP_DOC_TYPES} />
    </section>
  );
}

/* ---------- блок «Доставка» (раздел 14.2) ---------- */

function ConfirmDeliveryModal({ data, onClose, onDone }: { data: LotDetail; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.confirmDelivery(data.lot.id);
      onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Подтвердить доставку по ${data.lot.number}`} onClose={onClose}>
      <p>
        Заявка перейдёт в статус «{LOT_STATUS_LABELS.closed}». Будет создана задача на оплату со сроком{" "}
        <b>{data.delivery.paymentDueDays} {pluralDays(data.delivery.paymentDueDays)}</b> (календарных), а перевозчику <b>{data.winner?.company?.name ?? "—"}</b> уйдёт сообщение в Telegram с датой оплаты.
      </p>
      {!data.lot.deliveredAt && (
        <p className="hint warn">Перевозчик ещё не нажимал «Доставка завершена» в боте — датой доставки будет текущий момент.</p>
      )}
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Отмена
        </button>
        <button type="button" className="btn primary" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Подтверждаем…" : "Подтвердить доставку"}
        </button>
      </div>
    </Modal>
  );
}

function DeliveryBlock({ data, winner, onChanged }: { data: LotDetail; winner: LotWinner; onChanged: () => void }) {
  const { lot, delivery } = data;
  const [confirming, setConfirming] = useState(false);
  const [fixing, setFixing] = useState(false);
  const canConfirm = DELIVERY_CONFIRMABLE_STATUSES.includes(lot.status);
  const task = delivery.paymentTask;
  const due = task && !task.paidAt ? daysLeft(task.dueAt) : null;

  return (
    <section className="card delivery-block">
      <div className="row between">
        <h3>Доставка</h3>
        <span className={`badge ${statusBadgeClass(lot.status)}`}>{LOT_STATUS_LABELS[lot.status]}</span>
      </div>

      <div className="delivery-status">
        <div>
          <div className="k">Сообщение о доставке</div>
          <div className="v">{lot.deliveredAt ? fmtDateTime(lot.deliveredAt) : <span className="muted">ещё не получено</span>}</div>
        </div>
        <div>
          <div className="k">Подтверждена логистом</div>
          <div className="v">
            {lot.deliveryConfirmedAt ? fmtDateTime(lot.deliveryConfirmedAt) : <span className="muted">—</span>}
            {delivery.deliveryConfirmedByName && <div className="sub">{delivery.deliveryConfirmedByName}</div>}
          </div>
        </div>
        <div>
          <div className="k">Срок оплаты</div>
          <div className="v">
            {task ? (
              <>
                {fmtDateTime(task.dueAt).slice(0, 10)}
                {due !== null && <div className={`sub ${due < 0 ? "error" : ""}`}>{daysLeftLabel(due)}</div>}
              </>
            ) : (
              <span className="muted">после подтверждения</span>
            )}
          </div>
        </div>
        <div>
          <div className="k">Оплачено</div>
          <div className="v">
            {lot.paidAt ? fmtDateTime(lot.paidAt) : <span className="muted">—</span>}
            {delivery.paidByName && <div className="sub">{delivery.paidByName}</div>}
          </div>
        </div>
      </div>

      {canConfirm && (
        <>
          <p className="muted" style={{ margin: "0 0 4px" }}>
            Для подтверждения нужны ({TRANSPORT_KIND_LABELS[lot.transportKind].toLowerCase()}):
          </p>
          <ul className="missing-docs">
            {delivery.requiredDocs.map((t) => (
              <li key={t} className={delivery.missingDocs.includes(t) ? "" : "ok"}>
                {delivery.missingDocs.includes(t) ? "✗" : "✓"} {WINNER_DOC_LABELS[t]}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="actions" style={{ margin: "8px 0 12px" }}>
        {canConfirm && (
          <button
            className="btn primary"
            disabled={delivery.missingDocs.length > 0}
            title={delivery.missingDocs.length ? `Не хватает: ${delivery.missingDocs.map((t) => WINNER_DOC_LABELS[t]).join(", ")}` : undefined}
            onClick={() => setConfirming(true)}
          >
            Подтвердить доставку
          </button>
        )}
        {(lot.status === "closed" || lot.status === "paid") && (
          <>
            <a className="btn" href={api.paymentRequestPdfUrl(lot.id)}>
              Договор-заявка (PDF)
            </a>
            <a className="btn" href={api.packagePdfUrl(lot.id)} title="Договор-заявка + сканы всех документов победителя">
              Полный пакет (PDF)
            </a>
          </>
        )}
        {lot.status === "delivered" && (
          <button className="btn" onClick={() => setFixing(true)}>
            Вернуть на доработку
          </button>
        )}
        {lot.status === "closed" && task && (
          <span className="muted">Отметить оплату можно в блоке «К оплате» на главной.</span>
        )}
      </div>

      <h4>Документы доставки</h4>
      <WinnerDocuments lotId={lot.id} documents={winner.documents} stage="delivery" docTypes={DELIVERY_DOC_TYPES} />

      {confirming && (
        <ConfirmDeliveryModal
          data={data}
          onClose={() => setConfirming(false)}
          onDone={() => {
            setConfirming(false);
            onChanged();
          }}
        />
      )}
      {fixing && (
        <DeliveryFixModal
          data={data}
          onClose={() => setFixing(false)}
          onDone={() => {
            setFixing(false);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

/** «Вернуть на доработку» (14.2.4): перевозчику уходит текст, статус остаётся «Доставлено — проверка документов». */
function DeliveryFixModal({ data, onClose, onDone }: { data: LotDetail; onClose: () => void; onDone: () => void }) {
  const missing = data.delivery.missingDocs.map((t) => WINNER_DOC_LABELS[t]).join(", ");
  const [text, setText] = useState(missing ? `Не хватает: ${missing}` : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return setError("Напишите, каких документов не хватает");
    setBusy(true);
    setError(null);
    try {
      await api.requestDeliveryFix(data.lot.id, text.trim());
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Вернуть на доработку ${data.lot.number}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <p className="muted">
          Перевозчик <b>{data.winner?.company?.name ?? "—"}</b> получит в Telegram сообщение с этим текстом и сможет дослать документы через бота.
          Статус заявки не изменится.
        </p>
        <label className={`field${error ? " invalid" : ""}`}>
          <span>Что нужно дослать</span>
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
          {error && <small className="field-error">{error}</small>}
        </label>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Отправляем…" : "Отправить перевозчику"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------- документы победителя ---------- */

const fmtSize = (bytes: number | null) => {
  if (bytes === null) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
};

const ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function WinnerDocuments({
  lotId,
  documents: allDocuments,
  stage,
  docTypes,
}: {
  lotId: string;
  documents: LotWinner["documents"];
  /** Показываем и загружаем только документы этого этапа (до рейса / доставка). */
  stage: WinnerDocStage;
  docTypes: WinnerDocType[];
}) {
  const documents = allDocuments.filter((d) => d.stage === stage);
  const [docType, setDocType] = useState<WinnerDocType>(docTypes[0] ?? "other");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  const upload = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return setError("Выберите файл");
    setBusy(true);
    setError(null);
    try {
      await api.uploadWinnerDocument(lotId, docType, file, stage);
      setFile(null);
      setInputKey((k) => k + 1); // сбросить <input type=file>
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d: LotWinner["documents"][number]) => {
    if (!window.confirm(`Удалить документ «${d.fileName ?? WINNER_DOC_LABELS[d.docType]}»?`)) return;
    setError(null);
    try {
      await api.deleteWinnerDocument(lotId, d.id);
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <>
      {documents.length === 0 ? (
        <p className="muted">
          {stage === "pre_trip"
            ? "Документов пока нет. Удостоверение водителя и техпаспорт бот запрашивает у победителя в Telegram; можно загрузить и отсюда."
            : "Документов доставки пока нет. СМР, талон и счёт бот запрашивает у перевозчика после кнопки «Доставка завершена»; присланное почтой или WhatsApp загрузите отсюда."}
        </p>
      ) : (
        <ul className="docs">
          {documents.map((d) => (
            <li key={d.id}>
              <span>
                <b>{WINNER_DOC_LABELS[d.docType]}</b>
                <span className={`tag${d.source === "telegram" ? " tg" : ""}`}>{DOC_SOURCE_LABELS[d.source]}</span>
                <br />
                <span className="muted">
                  {d.fileName ?? "файл удалён"}
                  {d.sizeBytes !== null && ` · ${fmtSize(d.sizeBytes)}`} · {fmtDateTime(d.createdAt)}
                </span>
              </span>
              <span className="doc-actions">
                <a className="btn" href={api.fileUrl(d.fileId)} target="_blank" rel="noreferrer">
                  Открыть
                </a>
                <a className="btn" href={api.fileUrl(d.fileId, true)}>
                  Скачать
                </a>
                <button type="button" className="btn danger" onClick={() => void remove(d)}>
                  Удалить
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <form className="doc-upload" onSubmit={(e) => void upload(e)}>
        <select value={docType} onChange={(e) => setDocType(e.target.value as WinnerDocType)}>
          {docTypes.map((t) => (
            <option key={t} value={t}>
              {WINNER_DOC_LABELS[t]}
            </option>
          ))}
        </select>
        <input key={inputKey} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <button type="submit" className="btn primary" disabled={busy || !file}>
          {busy ? "Загружаем…" : "Загрузить"}
        </button>
      </form>
      <p className="hint">Фото (JPG, PNG), PDF и Word до 20 МБ. Документы видят только сотрудники портала.</p>
      {error && <p className="error">{error}</p>}
    </>
  );
}

/* ---------- журнал ---------- */

/** Подписи полей лота для события «Заявка изменена» (payload.fields из updateLot). */
const LOT_FIELD_LABELS: Record<string, string> = {
  projectId: "проект",
  transportKind: "тип перевозки",
  loadingAddress: "адрес погрузки",
  loadingAt: "дата погрузки",
  intermediateAddresses: "промежуточные адреса",
  unloadingAddress: "адрес выгрузки",
  vehicleTypeId: "тип транспорта",
  weightKg: "вес",
  lengthM: "габариты",
  widthM: "габариты",
  heightM: "габариты",
  placesCount: "грузовые места",
  cargoName: "груз",
  cargoDescription: "описание груза",
  specialConditions: "особые условия",
  insurance: "страхование",
  contactUserId: "контакт",
  deadlineAt: "дедлайн",
  autoExtend: "автопродление",
};

function eventDetails(e: LotDetailEvent): string | null {
  const p = e.payload;
  const str = (k: string) => (typeof p[k] === "string" ? (p[k] as string) : null);
  const numv = (k: string) => (typeof p[k] === "number" ? (p[k] as number) : null);
  switch (e.type) {
    case "edited": {
      const fields = Array.isArray(p.fields) ? [...new Set((p.fields as unknown[]).map((f) => LOT_FIELD_LABELS[String(f)] ?? String(f)))] : [];
      return fields.length ? fields.join(", ") : null;
    }
    case "extended_manual":
    case "extended_auto": {
      const to = str("to");
      const reason = str("reason");
      return [to ? `до ${fmtDateTime(to)}` : null, reason ? `причина: ${reason}` : null].filter(Boolean).join(" · ") || null;
    }
    case "cancelled":
      return str("reason") ? `причина: ${str("reason")}` : null;
    case "bid_accepted":
      return numv("amount") !== null ? formatTenge(numv("amount")!) : null;
    case "finished_deadline":
    case "finished_early":
      return numv("amount") !== null ? `победная ставка ${formatTenge(numv("amount")!)}` : "без победителя";
    case "delivery_confirmed":
      return str("dueAt") ? `срок оплаты до ${fmtDateTime(str("dueAt")!).slice(0, 10)}` : null;
    case "delivery_fix_requested":
      return str("text");
    default:
      return null;
  }
}

/* ---------- страница ---------- */

/* ---------- все ставки с авторами (только admin, раздел 10 требований) ---------- */

function AdminBidsBlock({ lotId, vat, version }: { lotId: string; vat: string; version: string }) {
  const [bids, setBids] = useState<AdminLotBid[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // version меняется при каждом обновлении лота (SSE/действия) — перечитываем вместе с карточкой.
  useEffect(() => {
    adminApi
      .lotBids(lotId)
      .then((rows) => {
        setBids(rows);
        setError(null);
      })
      .catch((e: unknown) => setError(errMsg(e)));
  }, [lotId, version]);

  return (
    <section className="card admin-bids">
      <h3>
        Все ставки (админ) {bids && <span className="muted">({bids.length})</span>}
      </h3>
      {error ? (
        <p className="error">{error}</p>
      ) : !bids ? (
        <p className="muted">Загрузка…</p>
      ) : bids.length === 0 ? (
        <p className="muted">Попыток ставок не было.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Время</th>
              <th>Компания</th>
              <th>Сумма</th>
              <th>Результат</th>
              <th>Минимум на момент</th>
            </tr>
          </thead>
          <tbody>
            {[...bids].reverse().map((b) => (
              <tr key={b.id} className={b.result === "accepted" ? "accepted" : "rejected"}>
                <td className="nowrap muted" title={fmtDateTime(b.placedAt)}>
                  {fmtDateTime(b.placedAt)}
                </td>
                <td>{b.companyName}</td>
                <td className="amount">
                  {formatTenge(b.amount)} <span className="muted">{vat}</span>
                </td>
                <td className="result">{BID_RESULT_LABELS[b.result]}</td>
                <td className="amount muted">{b.minAtTime === null ? "ставок не было" : formatTenge(b.minAtTime)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="hint">Авторов и отклонённые попытки видит только администратор; логистам этот блок не показывается.</p>
    </section>
  );
}

export function LotPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  const notice = localNotice ?? (location.state as { notice?: string } | null)?.notice ?? null;
  const [maxAutoExtensions, setMaxAutoExtensions] = useState<number | null>(null);
  useEffect(() => {
    api
      .publicSettings()
      .then((s) => setMaxAutoExtensions(s.auto_extend_max))
      .catch(() => setMaxAutoExtensions(null));
  }, []);

  const [data, setData] = useState<LotDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<Action>(null);
  const [busy, setBusy] = useState<"publish" | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const isAdmin = useAuth().user?.role === "admin";

  const load = (lotId: string) =>
    api
      .lot(lotId)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => setError(errMsg(e)));

  useEffect(() => {
    if (!id) return;
    void load(id);
    return subscribeEvents((type, payload) => {
      if (type === "lot.updated" && (payload as { lotId?: string }).lotId === id) void load(id);
    });
  }, [id]);

  const active = data?.lot.status === "active";
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);

  if (error && !data) {
    return (
      <>
        <div className="page-header">
          <h1>Карточка заявки</h1>
          <Link to="/" className="btn">
            К списку
          </Link>
        </div>
        <p className="error">{error}</p>
      </>
    );
  }
  if (!data || !id) return <p className="muted">Загрузка…</p>;

  const { lot, bids, recipients, events, winner } = data;
  const vat = vatLabel(lot.transportKind);
  const leader = bids.length ? bids[bids.length - 1] : null;
  const finished = FINISHED.includes(lot.status);
  // Требования, 8.3: редактирование доступно только пока нет ставок.
  const canEdit = (lot.status === "draft" || lot.status === "active") && bids.length === 0;
  const editHint =
    bids.length > 0
      ? "По лоту уже есть ставки — редактирование недоступно. Отмените лот и создайте копию."
      : lot.status === "active"
        ? "Редактировать можно до первой ставки; изменения обновят сообщения у перевозчиков."
        : null;

  const publish = async () => {
    setBusy("publish");
    try {
      await api.publishLot(lot.id);
      await load(lot.id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const closeAndReload = () => {
    setAction(null);
    void load(lot.id);
  };

  return (
    <>
      <div className="page-header">
        <div className="row">
          <h1>{lot.number}</h1>
          <span className={`badge ${statusBadgeClass(lot.status)}`}>
            {LOT_STATUS_LABELS[lot.status]}
          </span>
        </div>
        <Link to="/" className="btn">
          К списку
        </Link>
      </div>

      {notice && <p className="notice warn">{notice}</p>}
      {error && <p className="error">{error}</p>}

      <section className="card lot-head">
        <div>
          <div className="route">
            {lot.loadingAddress} → {lot.unloadingAddress}
          </div>
          <div className="sub">
            {lot.projectName} · {TRANSPORT_KIND_LABELS[lot.transportKind]} · {lot.vehicleTypeName} · погрузка {fmtDateTime(lot.loadingAt)}
          </div>
        </div>
        <div className="timer">
          {lot.status === "active" ? (
            <>
              <div className={`left${new Date(lot.deadlineAt).getTime() - now < 30 * 60_000 ? " soon" : ""}`}>{timeLeft(lot.deadlineAt, now)}</div>
              <div className="sub">до {fmtDateTime(lot.deadlineAt)}</div>
            </>
          ) : lot.status === "draft" ? (
            <>
              <div className="left muted">не опубликована</div>
              <div className="sub">дедлайн {fmtDateTime(lot.deadlineAt)}</div>
            </>
          ) : (
            <>
              <div className="left muted">торги завершены</div>
              <div className="sub">{fmtDateTime(lot.finishedAt ?? lot.deadlineAt)}</div>
            </>
          )}
          <div className="sub">
            {lot.autoExtend ? `автопродление ${lot.autoExtensionsUsed}${maxAutoExtensions !== null ? `/${maxAutoExtensions}` : ""}` : "без автопродления"}
          </div>
        </div>
        <div className="min-bid">
          {leader ? (
            <>
              <div className="price big">{formatTenge(leader.amount)}</div>
              <div className="sub">
                {vat} · ставок: {bids.length} · шаг {formatTenge(lot.minStep)}
              </div>
            </>
          ) : (
            <>
              <div className="price empty">ставок нет</div>
              <div className="sub">
                {vat} · шаг {formatTenge(lot.minStep)}
              </div>
            </>
          )}
        </div>
      </section>

      <div className="actions">
        {lot.status === "draft" && (
          <button className="btn primary" disabled={busy !== null} onClick={() => void publish()}>
            {busy === "publish" ? "Публикуем…" : "Опубликовать"}
          </button>
        )}
        {(lot.status === "draft" || lot.status === "active") && (
          <button className="btn" disabled={!canEdit} title={editHint ?? undefined} onClick={() => navigate(`/lots/new?edit=${lot.id}`)}>
            Редактировать
          </button>
        )}
        {lot.status === "active" && (
          <>
            <button className="btn" onClick={() => setAction("extend")}>
              Продлить
            </button>
            <button className="btn" onClick={() => setAction("finish")}>
              Завершить досрочно
            </button>
          </>
        )}
        {(lot.status === "draft" || lot.status === "active") && (
          <button className="btn danger" onClick={() => setAction("cancel")}>
            Отменить
          </button>
        )}
        <button className="btn" onClick={() => navigate(`/lots/new?copy=${lot.id}`)}>
          Создать копию
        </button>
        <button className="btn" onClick={() => setAction("template")}>
          Сохранить как шаблон
        </button>
        {(lot.status === "draft" || lot.status === "active") && editHint && <span className="muted">{editHint}</span>}
      </div>

      {lot.status === "cancelled" && (
        <p className="notice">
          Заявка отменена{lot.finishedAt ? ` ${fmtDateTime(lot.finishedAt)}` : ""}
          {lot.cancelReason ? `: ${lot.cancelReason}` : ""}. Нужна такая же перевозка — создайте копию.
        </p>
      )}
      {lot.status === "no_winner" && <p className="notice">Торги завершены без ставок. Можно создать копию и разослать заново.</p>}

      <div className="lot-grid">
        <div>
          {finished && winner && DELIVERY_STAGE_STATUSES.includes(lot.status) && (
            <DeliveryBlock data={data} winner={winner} onChanged={() => void load(lot.id)} />
          )}
          {finished && winner && <WinnerBlock data={data} winner={winner} />}

          <section className="card">
            <h3>Заявка</h3>
            <dl className="props">
              <dt>Проект</dt>
              <dd>{lot.projectName}</dd>
              <dt>Тип перевозки</dt>
              <dd>
                {TRANSPORT_KIND_LABELS[lot.transportKind]} <span className="muted">({vat})</span>
              </dd>
              <dt>Адрес погрузки</dt>
              <dd>{lot.loadingAddress}</dd>
              <dt>Дата и время погрузки</dt>
              <dd>{fmtDateTime(lot.loadingAt)}</dd>
              {lot.intermediateAddresses.length > 0 && (
                <>
                  <dt>Промежуточные адреса</dt>
                  <dd>
                    <ol className="plain">
                      {lot.intermediateAddresses.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ol>
                  </dd>
                </>
              )}
              <dt>Адрес выгрузки</dt>
              <dd>{lot.unloadingAddress}</dd>
              <dt>Тип транспорта</dt>
              <dd>{lot.vehicleTypeName}</dd>
              <dt>Вес</dt>
              <dd>{lot.weightKg.toLocaleString("ru-RU")} кг</dd>
              <dt>Габариты Д × Ш × В</dt>
              <dd>
                {formatDims(lot.lengthM, lot.widthM, lot.heightM)}
              </dd>
              <dt>Грузовых мест</dt>
              <dd>{lot.placesCount ?? "—"}</dd>
              <dt>Груз</dt>
              <dd>
                {lot.cargoName}
                {lot.cargoDescription && <div className="muted pre">{lot.cargoDescription}</div>}
              </dd>
              <dt>Особые условия</dt>
              <dd className="pre">{lot.specialConditions || "—"}</dd>
              <dt>Страхование</dt>
              <dd className="pre">{lot.insurance || "—"}</dd>
              <dt>Окончание приёма ставок</dt>
              <dd>
                {fmtDateTime(lot.deadlineAt)} <span className="muted">({APP_TZ})</span>
              </dd>
              <dt>Автопродление</dt>
              <dd>{lot.autoExtend ? `да, использовано ${lot.autoExtensionsUsed} из 2` : "нет"}</dd>
              <dt>Создал</dt>
              <dd>
                {lot.createdByName} · {fmtDateTime(lot.createdAt)}
              </dd>
              {lot.publishedAt && (
                <>
                  <dt>Опубликована</dt>
                  <dd>{fmtDateTime(lot.publishedAt)}</dd>
                </>
              )}
            </dl>
          </section>

          <section className="card">
            <h3>
              Ставки <span className="muted">({bids.length})</span>
            </h3>
            {bids.length === 0 ? (
              <p className="muted">Ставок ещё не было.</p>
            ) : (
              <ol className="bids">
                {[...bids].reverse().map((b) => (
                  <li key={b.n} className={b.n === leader?.n ? "lead" : ""}>
                    <span>Ставка № {b.n}</span>
                    <span className="amount">
                      {formatTenge(b.amount)} <span className="muted">{vat}</span>
                    </span>
                    <span className="muted" title={fmtDateTime(b.placedAt)}>
                      {fmtTime(b.placedAt)}
                    </span>
                    {b.n === leader?.n && <span className="tag">минимум</span>}
                  </li>
                ))}
              </ol>
            )}
            <p className="hint">Авторы ставок логисту не показываются — только суммы и время.</p>
          </section>

          {isAdmin && <AdminBidsBlock lotId={lot.id} vat={vat} version={`${lot.updatedAt}:${bids.length}:${lot.status}`} />}
        </div>

        <div>
          {lot.status !== "draft" && <LotChat lotId={lot.id} lotStatus={lot.status} />}

          <section className="card">
            <h3>
              Получатели <span className="muted">({recipients.length})</span>
            </h3>
            <ul className="recipients-list">
              {recipients.map((r) => (
                <li key={r.companyId}>
                  <span className="name">{r.companyName}</span>
                  <span className={`delivery ${r.deliveryStatus}`} title={r.error ?? undefined}>
                    {lot.status === "draft" && r.deliveryStatus === "pending" ? "после публикации" : DELIVERY_STATUS_LABELS[r.deliveryStatus]}
                    {r.sentAt && ` · ${fmtTime(r.sentAt)}`}
                  </span>
                  {r.error && <span className="sub error">{r.error}</span>}
                </li>
              ))}
            </ul>
          </section>

          <section className="card">
            <h3>Журнал</h3>
            <ul className="events">
              {[...events].reverse().map((e) => {
                const details = eventDetails(e);
                return (
                  <li key={e.id}>
                    <span className="when">{fmtDateTime(e.createdAt)}</span>
                    <span>
                      {LOT_EVENT_LABELS[e.type]}
                      {details && <span className="muted"> — {details}</span>}
                      {e.actorName && <span className="sub">{e.actorName}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </div>

      {action === "extend" && <ExtendModal lot={lot} onClose={() => setAction(null)} onDone={closeAndReload} />}
      {action === "finish" && <FinishModal data={data} onClose={() => setAction(null)} onDone={closeAndReload} />}
      {action === "cancel" && <CancelModal lot={lot} onClose={() => setAction(null)} onDone={closeAndReload} />}
      {action === "template" && (
        <SaveTemplateModal
          lot={lot}
          onClose={() => setAction(null)}
          onDone={(name) => {
            setAction(null);
            setLocalNotice(`Шаблон «${name}» сохранён — Справочники → Шаблоны заявок.`);
          }}
        />
      )}
    </>
  );
}
